import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

interface EmailTemplate {
    id: string;
    name: string;
    subject: string;
    htmlContent: string;
    isActive: boolean;
    brevoTemplateId?: number | null; // ID no Brevo (null = não sincronizado)
    createdAt: string;
    updatedAt: string;
}

interface EmailTemplatesManagerProps {
    tenantId?: string;
    isGlobal?: boolean; // When true, templates are global (not tenant-specific)
}

export function EmailTemplatesManager({ tenantId, isGlobal = false }: EmailTemplatesManagerProps) {
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [availableTemplates, setAvailableTemplates] = useState<EmailTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        type?: 'danger' | 'info';
        confirmText?: string;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'info'
    });

    const showConfirm = (config: {
        title: string;
        message: string;
        onConfirm: () => void;
        type?: 'danger' | 'info';
        confirmText?: string;
    }) => {
        setConfirmDialog({
            isOpen: true,
            ...config
        });
    };

    const [formData, setFormData] = useState({
        name: '',
        subject: '',
        htmlContent: ''
    });

    const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
        const token = localStorage.getItem('auth_token');
        const selectedTenantId = localStorage.getItem('selected_tenant_id') || tenantId;
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            (headers as Record<string, string>).Authorization = `Bearer ${token}`;
        }

        if (selectedTenantId) {
            (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
        }

        return fetch(url, { ...options, headers });
    }, [tenantId]);

    const loadTemplates = useCallback(async () => {
        try {
            // Use global endpoint for SuperAdmin, tenant-specific otherwise
            const endpoint = isGlobal ? '/api/admin/email/templates?global=true' : '/api/admin/email/templates';
            const response = await authenticatedFetch(endpoint);
            if (response.ok) {
                const data = await response.json();
                setTemplates(data.templates || []);
            } else if (response.status === 403) {
                toast.error('Acesso negado. Apenas SUPERADMIN pode gerenciar templates.');
            }
        } catch (error) {
            console.error('Erro ao carregar templates:', error);
            toast.error('Erro ao carregar templates de email');
        } finally {
            setLoading(false);
        }
    }, [isGlobal, authenticatedFetch]);

    const loadAvailableTemplates = useCallback(async () => {
        try {
            const response = await authenticatedFetch('/api/email/templates');
            if (response.ok) {
                const data = await response.json();
                setAvailableTemplates(data.templates || []);
            }
        } catch (error) {
            console.error('Erro ao carregar templates disponíveis:', error);
        }
    }, [authenticatedFetch]);

    useEffect(() => {
        loadTemplates();
        loadAvailableTemplates();
    }, [loadTemplates, loadAvailableTemplates]);

    const handleCreate = async () => {
        if (!formData.name || !formData.subject || !formData.htmlContent) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }

        try {
            const response = await authenticatedFetch('/api/admin/email/templates', {
                method: 'POST',
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                toast.success('Template criado com sucesso!');
                setIsModalOpen(false);
                setFormData({ name: '', subject: '', htmlContent: '' });
                loadTemplates();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao criar template');
            }
        } catch (error) {
            console.error('Erro ao criar template:', error);
            toast.error('Erro ao criar template');
        }
    };

    const handleUpdate = async () => {
        if (!selectedTemplate) return;

        try {
            const response = await authenticatedFetch(`/api/admin/email/templates/${selectedTemplate.id}`, {
                method: 'PUT',
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                toast.success('Template atualizado com sucesso!');
                setIsModalOpen(false);
                setSelectedTemplate(null);
                setFormData({ name: '', subject: '', htmlContent: '' });
                loadTemplates();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao atualizar template');
            }
        } catch (error) {
            console.error('Erro ao atualizar template:', error);
            toast.error('Erro ao atualizar template');
        }
    };

    const handleDelete = async (id: string) => {
        showConfirm({
            title: 'Excluir Template',
            message: 'Tem certeza que deseja excluir este template? Esta ação não pode ser desfeita.',
            confirmText: 'Excluir',
            type: 'danger',
            onConfirm: async () => {
                try {
                    const response = await authenticatedFetch(`/api/admin/email/templates/${id}`, {
                        method: 'DELETE'
                    });

                    if (response.ok) {
                        toast.success('Template excluído com sucesso!');
                        loadTemplates();
                    } else {
                        const error = await response.json();
                        toast.error(error.error || 'Erro ao excluir template');
                    }
                } catch (error) {
                    console.error('Erro ao excluir template:', error);
                    toast.error('Erro ao excluir template');
                }
            }
        });
    };

    const handleToggle = async (id: string) => {
        try {
            const response = await authenticatedFetch(`/api/admin/email/templates/${id}/toggle`, {
                method: 'PATCH'
            });

            if (response.ok) {
                toast.success('Status atualizado!');
                loadTemplates();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao atualizar status');
            }
        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            toast.error('Erro ao atualizar status');
        }
    };

    const handlePreview = async (templateName: string) => {
        setPreviewLoading(true);
        setIsPreviewOpen(true);

        try {
            const response = await authenticatedFetch(`/api/email/preview/${templateName}`);
            if (response.ok) {
                const html = await response.text();
                setPreviewHtml(html);
            } else {
                setPreviewHtml('<p style="color: red; text-align: center; padding: 40px;">Erro ao carregar preview</p>');
            }
        } catch (error) {
            console.error('Erro ao carregar preview:', error);
            setPreviewHtml('<p style="color: red; text-align: center; padding: 40px;">Erro ao carregar preview</p>');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handlePreviewTemplate = (template: EmailTemplate) => {
        setPreviewHtml(template.htmlContent);
        setIsPreviewOpen(true);
    };

    const handleSyncBrevo = async (template: EmailTemplate) => {
        try {
            toast.loading('Sincronizando com Brevo...');
            const response = await authenticatedFetch(`/api/admin/email/templates/${template.id}/sync-brevo`, {
                method: 'POST'
            });

            if (response.ok) {
                toast.dismiss();
                toast.success('Template sincronizado com Brevo!');
                loadTemplates();
            } else {
                const error = await response.json();
                toast.dismiss();
                toast.error(error.error || 'Erro ao sincronizar com Brevo');
            }
        } catch (error) {
            console.error('Erro ao sincronizar com Brevo:', error);
            toast.dismiss();
            toast.error('Erro ao sincronizar com Brevo');
        }
    };

    const openEditModal = (template: EmailTemplate) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            subject: template.subject,
            htmlContent: template.htmlContent
        });
        setIsModalOpen(true);
    };

    const openCreateModal = () => {
        setSelectedTemplate(null);
        setFormData({ name: '', subject: '', htmlContent: '' });
        setIsModalOpen(true);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                <div className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando templates...</div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/5 p-8 rounded-[2rem] border border-white/10 backdrop-blur-md relative overflow-hidden group/header">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent opacity-0 group-hover/header:opacity-100 transition-opacity" />
                <div className="relative z-10">
                    <h2 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight">Templates de Email</h2>
                    <p className="text-[10px] font-bold text-gray-300 mt-2 uppercase tracking-[0.2em] font-oswald">Gerencie os templates de email transacionais do sistema</p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="relative z-10 px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-xs hover:bg-blue-500 shadow-lg shadow-blue-900/20 hover:shadow-blue-500/40 hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 group/btn overflow-hidden border border-blue-400/50"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                    <span className="text-lg group-hover:rotate-12 transition-transform">➕</span> Novo Template
                </button>
            </div>

            {/* Available Templates Preview */}
            {availableTemplates.length > 0 && (
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 relative overflow-hidden group/system">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-[80px] -mr-32 -mt-32 group-hover/system:bg-blue-600/10 transition-colors" />
                    <h3 className="font-bold font-oswald text-white mb-6 uppercase tracking-[0.2em] text-[10px] flex items-center gap-3">
                        <span className="p-1.5 bg-blue-500/10 rounded-lg text-blue-400 border border-blue-500/20">📧</span> Templates do Sistema
                    </h3>
                    <div className="flex flex-wrap gap-4 relative z-10">
                        {availableTemplates.map((name) => (
                            <button
                                key={name}
                                onClick={() => handlePreview(name)}
                                className="px-6 py-3 bg-white/5 rounded-xl text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] hover:bg-blue-600/10 hover:text-blue-400 hover:border-blue-500/30 transition-all border border-white/10 font-oswald"
                            >
                                {name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Templates List */}
            {templates.length === 0 ? (
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[3rem] p-20 text-center relative overflow-hidden group/empty">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-600/5 to-transparent opacity-0 group-hover/empty:opacity-100 transition-opacity" />
                    <div className="w-24 h-24 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-white/10 shadow-inner relative z-10">
                        <span className="text-5xl opacity-50 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500">📧</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4 font-oswald uppercase tracking-tight relative z-10">Nenhum template personalizado</h3>
                    <p className="text-gray-300 mb-10 max-w-md mx-auto font-oswald text-[10px] font-bold uppercase tracking-[0.2em] leading-relaxed relative z-10">
                        Crie templates customizados para suas campanhas de email e melhore a comunicação com seus clientes
                    </p>
                    <button
                        onClick={openCreateModal}
                        className="relative z-10 px-10 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-xs hover:bg-blue-500 shadow-xl shadow-blue-900/40 hover:-translate-y-1 transition-all group/btn overflow-hidden border border-blue-400/50"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                        Criar Primeiro Template
                    </button>
                </div>
            ) : (
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl relative">
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-white/5 border-b border-white/10">
                                    <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Template</th>
                                    <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Assunto</th>
                                    <th className="px-8 py-6 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Brevo</th>
                                    <th className="px-8 py-6 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Status</th>
                                    <th className="px-8 py-6 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {templates.map((template) => (
                                    <tr key={template.id} className="hover:bg-white/[0.02] transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="font-bold text-white text-base font-oswald uppercase tracking-wide">{template.name}</div>
                                            <div className="text-[10px] font-bold text-gray-400 mt-2 group-hover:text-blue-400/60 transition-colors uppercase tracking-widest font-oswald">
                                                ID: {template.id.slice(0, 8)}... • {new Date(template.updatedAt).toLocaleDateString('pt-BR')}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="text-gray-200 text-sm font-medium max-w-xs truncate font-oswald uppercase tracking-wide">{template.subject}</div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            {template.brevoTemplateId ? (
                                                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold bg-green-500/10 text-green-400 border border-green-500/20 shadow-sm font-oswald uppercase tracking-wider">
                                                    ✓ Sincronizado
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold bg-white/5 text-gray-300 border border-white/10 font-oswald uppercase tracking-wider">
                                                    Pendente
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <button
                                                onClick={() => handleToggle(template.id)}
                                                className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold transition-all uppercase tracking-wider font-oswald border ${template.isActive
                                                    ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border-green-500/30'
                                                    : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border-red-500/30'
                                                    }`}
                                            >
                                                {template.isActive ? 'Ativo' : 'Inativo'}
                                            </button>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center justify-center gap-3">
                                                <button
                                                    onClick={() => handlePreviewTemplate(template)}
                                                    className="w-10 h-10 flex items-center justify-center text-blue-400 bg-blue-500/5 hover:bg-blue-500/20 rounded-xl transition-all border border-blue-500/20 group/icon"
                                                    title="Visualizar"
                                                >
                                                    <span className="text-lg group-hover/icon:scale-110 transition-transform">👁️</span>
                                                </button>
                                                <button
                                                    onClick={() => handleSyncBrevo(template)}
                                                    className="w-10 h-10 flex items-center justify-center text-cyan-400 bg-cyan-500/5 hover:bg-cyan-500/20 rounded-xl transition-all border border-cyan-500/20 group/icon"
                                                    title="Sincronizar com Brevo"
                                                >
                                                    <span className="text-lg group-hover/icon:rotate-180 transition-transform duration-500">🔄</span>
                                                </button>
                                                <button
                                                    onClick={() => openEditModal(template)}
                                                    className="w-10 h-10 flex items-center justify-center text-amber-400 bg-amber-500/5 hover:bg-amber-500/20 rounded-xl transition-all border border-amber-500/20 group/icon"
                                                    title="Editar"
                                                >
                                                    <span className="text-lg group-hover/icon:scale-110 transition-transform">✏️</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(template.id)}
                                                    className="w-10 h-10 flex items-center justify-center text-red-400 bg-red-500/5 hover:bg-red-500/20 rounded-xl transition-all border border-red-500/20 group/icon"
                                                    title="Excluir"
                                                >
                                                    <span className="text-lg group-hover/icon:scale-110 transition-transform">🗑️</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-[#0f172a]/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-white/10 max-h-[90vh] flex flex-col relative group/modal">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover/modal:opacity-100 transition-opacity duration-500" />
                        
                        <div className="px-8 py-7 bg-white/5 border-b border-white/10 flex justify-between items-center backdrop-blur-md relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-blue-500/10 rounded-xl border border-blue-500/20 flex items-center justify-center shadow-[0_0_15px_rgba(37,99,235,0.2)]">
                                    <span className="text-xl">📧</span>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold font-oswald text-white uppercase tracking-tight">
                                        {selectedTemplate ? 'Editar Template' : 'Novo Template'}
                                    </h3>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Configuração do layout de email</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-xl border border-white/10"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-8 space-y-8 overflow-y-auto flex-1 custom-scrollbar relative z-10">
                            <div className="space-y-3">
                                <label className="block text-[10px] font-bold font-oswald text-gray-300 uppercase tracking-[0.2em]">
                                    Nome do Template *
                                </label>
                                <div className="relative group/input">
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Ex: Boas Vindas"
                                        className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all text-white font-oswald placeholder-gray-500 tracking-wide"
                                    />
                                    <div className="absolute inset-0 rounded-2xl bg-blue-500/5 opacity-0 group-hover/input:opacity-100 pointer-events-none transition-opacity" />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="block text-[10px] font-bold font-oswald text-gray-300 uppercase tracking-[0.2em]">
                                    Assunto do Email *
                                </label>
                                <div className="relative group/input">
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        placeholder="Ex: Bem-vindo à nossa plataforma!"
                                        className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all text-white font-oswald placeholder-gray-500 tracking-wide"
                                    />
                                    <div className="absolute inset-0 rounded-2xl bg-blue-500/5 opacity-0 group-hover/input:opacity-100 pointer-events-none transition-opacity" />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="block text-[10px] font-bold font-oswald text-gray-300 uppercase tracking-[0.2em]">
                                    Conteúdo HTML *
                                </label>
                                <div className="relative group/input">
                                    <textarea
                                        value={formData.htmlContent}
                                        onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                                        placeholder="<html>...</html>"
                                        rows={12}
                                        className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all font-mono text-sm text-gray-200 placeholder-gray-500 custom-scrollbar resize-none"
                                    />
                                    <div className="absolute inset-0 rounded-2xl bg-blue-500/5 opacity-0 group-hover/input:opacity-100 pointer-events-none transition-opacity" />
                                </div>
                                <div className="flex items-center gap-2 px-2">
                                    <span className="text-[10px] font-bold text-blue-400 font-oswald uppercase tracking-widest">Dica:</span>
                                    <p className="text-[10px] text-gray-400 font-oswald uppercase tracking-widest">Use tags como {'{{name}}'} para personalização.</p>
                                </div>
                            </div>
                        </div>

                        <div className="px-8 py-7 bg-white/5 border-t border-white/10 flex justify-end gap-4 backdrop-blur-md relative z-10">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-8 py-3.5 text-gray-300 bg-white/5 rounded-xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] hover:bg-white/10 hover:text-white transition-all border border-white/10"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={selectedTemplate ? handleUpdate : handleCreate}
                                className="px-10 py-3.5 bg-blue-600 text-white rounded-xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] hover:bg-blue-500 shadow-xl shadow-blue-900/40 hover:-translate-y-1 transition-all group/btn overflow-hidden border border-blue-400/50"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                {selectedTemplate ? 'Salvar Alterações' : 'Criar Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {isPreviewOpen && (
                <div className="fixed inset-0 bg-[#0f172a]/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full max-w-5xl overflow-hidden border border-white/10 h-[85vh] flex flex-col relative group/preview">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover/preview:opacity-100 transition-opacity duration-500" />
                        
                        <div className="px-8 py-6 bg-white/5 border-b border-white/10 flex justify-between items-center backdrop-blur-md relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-blue-500/10 rounded-xl border border-blue-500/20 flex items-center justify-center shadow-[0_0_15px_rgba(37,99,235,0.2)]">
                                    <span className="text-lg">👁️</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold font-oswald text-white uppercase tracking-tight">
                                        Visualizar Template
                                    </h3>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Pré-visualização do conteúdo final</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsPreviewOpen(false)}
                                className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-xl border border-white/10"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex-1 bg-white m-8 rounded-2xl overflow-hidden shadow-inner relative z-10 border border-black/10">
                            {previewLoading ? (
                                <div className="h-full flex flex-col items-center justify-center gap-4 bg-[#0f172a]/5 backdrop-blur-sm">
                                    <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                                    <span className="text-[10px] font-bold text-blue-500 font-oswald uppercase tracking-[0.2em]">Carregando preview...</span>
                                </div>
                            ) : (
                                <iframe
                                    srcDoc={previewHtml}
                                    className="w-full h-full border-0"
                                    title="Email Preview"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação Standardizado */}
            <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
                <AlertDialogContent className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 backdrop-blur-3xl shadow-2xl max-w-md">
                    <AlertDialogHeader className="space-y-4">
                        <AlertDialogTitle className="text-2xl font-bold font-oswald text-white uppercase tracking-tight flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${confirmDialog.type === 'danger' ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-blue-500/10 border-blue-500/20 text-blue-400'}`}>
                                {confirmDialog.type === 'danger' ? '⚠️' : 'ℹ️'}
                            </div>
                            {confirmDialog.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-300 font-oswald uppercase tracking-[0.1em] text-[10px] font-bold leading-relaxed">
                            {confirmDialog.message}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-8 flex gap-3">
                        <AlertDialogCancel className="flex-1 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-bold font-oswald text-gray-300 uppercase tracking-[0.2em] transition-all">
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDialog.onConfirm}
                            className={`flex-1 px-8 py-4 rounded-2xl text-[10px] font-bold font-oswald text-white uppercase tracking-[0.2em] transition-all border shadow-lg ${confirmDialog.type === 'danger'
                                ? 'bg-red-600 hover:bg-red-500 border-red-500 shadow-red-900/20'
                                : 'bg-blue-600 hover:bg-blue-500 border-blue-500 shadow-blue-900/20'
                                }`}
                        >
                            {confirmDialog.confirmText || 'Confirmar'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default EmailTemplatesManager;
